import { type AuthConfig, authExchange as urqlAuthExchange } from '@urql/exchange-auth';
import { type Exchange } from 'urql';

import { AZURE_API_ACCESS_TOKEN_HEADER } from '../../../AzureAuth/config';
import { refreshHybridFlowApiAccessToken } from '../../../AzureAuth/helpers/refresh-api-access-token';

/**
 * Urql Auth Exchange
 *
 * Reads from storage and adds authorization headers to the outgoing request.
 *
 * @see {@link: https://formidable.com/open-source/urql/docs/advanced/authentication/}
 * @see {@link: https://formidable.com/open-source/urql/docs/api/auth-exchange/}
 */
export const authExchange: Exchange = urqlAuthExchange(async (utils): Promise<AuthConfig> => {
  const authState = await getAuthState();

  /**
   * Mutable token reference that should store a fresh token.
   *
   * @see {@link https://commerce.nearform.com/open-source/urql/docs/advanced/authentication/#configuring-refreshauth-triggered-after-an-auth-error-has-occurred}
   */

  let azureAdAccessToken = authState?.azureAdAccessToken;

  // ─────────────────────────────────────────────────────────────────────

  return {
    /**
     * Appends corresponding authentication header/s to the outgoing request.
     */
    addAuthToOperation(operation) {
      const azureAdAccessTokenHeader = azureAdAccessToken
        ? { [AZURE_API_ACCESS_TOKEN_HEADER]: azureAdAccessToken }
        : undefined;

      return utils.appendHeaders(operation, {
        ...azureAdAccessTokenHeader,
      });
    },

    /**
     * Runs before a network request is made.
     *
     * Returning `true` forces the `refreshAuth` to re-read from storage
     * every request (assumes that the logout workflow clears/updates the token from storage)
     */
    willAuthError() {
      return true;
    },

    /**
     * Gets fresh auth state from storage and assigns it to the
     * mutable reference.
     */
    async refreshAuth() {
      const updatedAuthState = await getAuthState();

      azureAdAccessToken = updatedAuthState?.azureAdAccessToken;
    },

    /**
     * We set `didAuthError` to a no-op because it's required by the `AuthConfig`.
     */
    didAuthError() {
      return false;
    },
  };
});

// ─── Helpers ─────────────────────────────────────────────────────────────────

async function getAuthState(): Promise<AuthState> {
  try {
    const azureAdAccessToken = await refreshHybridFlowApiAccessToken();

    return {
      azureAdAccessToken,
    };
  } catch {
    return {};
  }
}

// ─── Types ───────────────────────────────────────────────────────────────────

type AuthState = {
  azureAdAccessToken?: string | null;
};
